import React, { useState } from 'react';
import { Loader } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilter } from '@fortawesome/free-solid-svg-icons';
import { ProjectWithVideos, SocialPost, PublishState } from './types';
import SocialMediaProjectCard from './SocialMediaProjectCard';
import styles from './SocialMediaLanding.module.css';

interface ProjectGridSectionProps {
  projects: ProjectWithVideos[];
  isLoading: boolean;
  error: string | null;
  searchTerm: string;
  onSearchChange: (value: string) => void;
  onShare: (project: ProjectWithVideos, selectedVideoUrl: string | null) => void;
  publishedPosts: SocialPost[]; // All published posts
}

type FilterType = 'all' | 'published' | 'unpublished';

const ProjectGridSection: React.FC<ProjectGridSectionProps> = ({
  projects,
  isLoading,
  error,
  searchTerm,
  onSearchChange,
  onShare,
  publishedPosts
}) => {
  const [filter, setFilter] = useState<FilterType>('all');
  const { t } = useTranslation('socialMediaLanding');
  
  // Create a map of projectId -> posts for quick lookup
  const postsByProject = publishedPosts.reduce((acc, post) => {
    if (!acc[post.projectId]) {
      acc[post.projectId] = [];
    }
    acc[post.projectId].push(post);
    return acc;
  }, {} as Record<string, SocialPost[]>);
  
  // Filter projects that have at least one video
  const projectsWithVideos = projects.filter(project => 
    project.originalVideoUrl || project.captionedVideoUrl || project.generatedVideoUrl
  );

  // Apply search filter
  let filteredProjects = projectsWithVideos.filter(project =>
    project.title.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  // Apply publish status filter
  if (filter === 'published') {
    filteredProjects = filteredProjects.filter(project => 
      postsByProject[project.id]?.length > 0
    );
  } else if (filter === 'unpublished') {
    filteredProjects = filteredProjects.filter(project => 
      !postsByProject[project.id] || postsByProject[project.id].length === 0
    );
  }
  
  // Calculate counts for each filter
  const allCount = projectsWithVideos.filter(project =>
    project.title.toLowerCase().includes(searchTerm.toLowerCase())
  ).length;
  
  const publishedCount = projectsWithVideos.filter(project =>
    project.title.toLowerCase().includes(searchTerm.toLowerCase()) &&
    postsByProject[project.id]?.length > 0
  ).length;
  
  const unpublishedCount = projectsWithVideos.filter(project =>
    project.title.toLowerCase().includes(searchTerm.toLowerCase()) &&
    (!postsByProject[project.id] || postsByProject[project.id].length === 0)
  ).length;

  if (isLoading) {
    return (
      <div className={styles.projectsSection}>
        <div className={styles.loadingContainer}>
          <Loader size="xl" />
          <p>{t('projects.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.projectsSection}>
        <div className={styles.errorContainer}>
          <p className={styles.errorMessage}>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.projectsSection}>
      <div className={styles.sectionHeader}>        
        <div className={styles.searchBar}>
          <input
            type="text"
            placeholder={t('projects.search')}
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            className={styles.searchInput}
          />
        </div>
        
        {/* Filter buttons */}
        <div className={styles.filterButtons}>
          <FontAwesomeIcon icon={faFilter} className={styles.filterIcon} />
          <button
            className={`${styles.filterButton} ${filter === 'all' ? styles.filterButtonActive : ''}`}
            onClick={() => setFilter('all')}
          >
            {t('projects.filters.all')} ({allCount})
          </button>
          <button
            className={`${styles.filterButton} ${filter === 'published' ? styles.filterButtonActive : ''}`}
            onClick={() => setFilter('published')}
          >
            {t('projects.filters.published')} ({publishedCount})
          </button>
          <button
            className={`${styles.filterButton} ${filter === 'unpublished' ? styles.filterButtonActive : ''}`}
            onClick={() => setFilter('unpublished')}
          >
            {t('projects.filters.unpublished')} ({unpublishedCount})
          </button>
        </div>
      </div>

      {projectsWithVideos.length === 0 ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyStateIcon}>📹</div>
          <h3>{t('projects.emptyState.noVideos.title')}</h3>
          <p>
            {t('projects.emptyState.noVideos.description')}
            <br />
            <a href="/create-content">{t('projects.emptyState.noVideos.linkText')}</a>{t('projects.emptyState.noVideos.linkSuffix')}
          </p>
        </div>
      ) : filteredProjects.length === 0 ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyStateIcon}>🔍</div>
          <h3>{t('projects.emptyState.noMatches.title')}</h3>
          <p>{t('projects.emptyState.noMatches.description')}</p>
        </div>
      ) : (
        <>
          <div className={styles.projectStats}>
            <span>{t('projects.available', { count: filteredProjects.length })}</span>
          </div>
          
          <div className={styles.projectGrid}>
            {filteredProjects.map(project => (
              <SocialMediaProjectCard
                key={project.id}
                project={project}
                onShare={onShare}
                publishedPosts={postsByProject[project.id] || []}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default ProjectGridSection;
